import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import paymentReportsService from '../../services/paymentReportsService';
import SecurePaymentDetailsModal from './SecurePaymentDetailsModal';
import './PaymentReports.css';

const PaymentReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showSecureModal, setShowSecureModal] = useState(false);
  
  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  const [linkAnalytics, setLinkAnalytics] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [pagination, setPagination] = useState({});
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    status: '',
    payment_method: '',
    agent_filter: '',
    search: '',
    page: 1,
    limit: 20
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchPaymentHistory();
    }
  }, [filters, activeTab]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [analyticsData, linkAnalyticsData] = await Promise.all([
        paymentReportsService.getAnalytics(filters),
        paymentReportsService.getPaymentLinksAnalytics(filters)
      ]);
      
      setAnalytics(analyticsData.analytics);
      setLinkAnalytics(linkAnalyticsData.link_analytics);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load payment analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const historyData = await paymentReportsService.getPaymentHistory(filters);
      setPaymentHistory(historyData.payments);
      setPagination(historyData.pagination);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setError('Failed to load payment history');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await paymentReportsService.exportPaymentData(filters);
      alert('Payment report exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export payment report');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setShowSecureModal(true);
  };

  const closeSecureModal = () => {
    setShowSecureModal(false);
    setSelectedPayment(null);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: 'status-completed',
      pending: 'status-pending',
      failed: 'status-failed',
      refunded: 'status-refunded',
      expired: 'status-expired',
      cancelled: 'status-cancelled'
    };
    
    return <span className={`status-badge ${statusClasses[status] || ''}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="payment-reports-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading payment reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-reports-container">
        <div className="error-message">
          <h3>Error Loading Reports</h3>
          <p>{error}</p>
          <button onClick={fetchAllData} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-reports-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>💰 Payment Reports & Analytics</h1>
          <p className="page-description">
            Comprehensive payment analytics, trends, and reporting dashboard
          </p>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-secondary"
          >
            {exporting ? '📤 Exporting...' : '📊 Export CSV'}
          </button>
          <button 
            onClick={fetchAllData}
            className="btn btn-primary"
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Payment Method:</label>
          <select
            value={filters.payment_method}
            onChange={(e) => handleFilterChange('payment_method', e.target.value)}
            className="filter-select"
          >
            <option value="">All Methods</option>
            <option value="card">Credit Card</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="check">Check</option>
            <option value="cash">Cash</option>
          </select>
        </div>
        <button 
          onClick={fetchAllData}
          className="btn btn-primary filter-apply-btn"
        >
          Apply Filters
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button 
            className={`tab ${activeTab === 'payment-links' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment-links')}
          >
            🔗 Payment Links
          </button>
          <button 
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📋 Payment History
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && analytics && (
          <div className="overview-tab">
            {/* Key Metrics */}
            <div className="metrics-grid">
              <div className="metric-card revenue">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  <h3>Total Revenue</h3>
                  <div className="metric-value">{formatCurrency(analytics.total_revenue)}</div>
                  <div className="metric-subtitle">
                    {analytics.completed_payments} completed payments
                  </div>
                </div>
              </div>
              
              <div className="metric-card pending">
                <div className="metric-icon">⏳</div>
                <div className="metric-content">
                  <h3>Pending Revenue</h3>
                  <div className="metric-value">{formatCurrency(analytics.pending_revenue)}</div>
                  <div className="metric-subtitle">
                    {analytics.pending_payments} pending payments
                  </div>
                </div>
              </div>
              
              <div className="metric-card average">
                <div className="metric-icon">📊</div>
                <div className="metric-content">
                  <h3>Average Payment</h3>
                  <div className="metric-value">{formatCurrency(analytics.avg_payment_amount)}</div>
                  <div className="metric-subtitle">
                    {analytics.total_payments} total payments
                  </div>
                </div>
              </div>
              
              <div className="metric-card failed">
                <div className="metric-icon">❌</div>
                <div className="metric-content">
                  <h3>Failed Payments</h3>
                  <div className="metric-value">{analytics.failed_payments}</div>
                  <div className="metric-subtitle">
                    {formatCurrency(analytics.refunded_amount)} refunded
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method Breakdown */}
            <div className="charts-section">
              <div className="chart-card">
                <h3>Payment Methods</h3>
                <div className="payment-methods-grid">
                  <div className="method-item">
                    <span className="method-label">💳 Credit Card</span>
                    <span className="method-count">{analytics.card_payments}</span>
                  </div>
                  <div className="method-item">
                    <span className="method-label">🏦 Bank Transfer</span>
                    <span className="method-count">{analytics.bank_payments}</span>
                  </div>
                  <div className="method-item">
                    <span className="method-label">📝 Check</span>
                    <span className="method-count">{analytics.check_payments}</span>
                  </div>
                  <div className="method-item">
                    <span className="method-label">💵 Cash</span>
                    <span className="method-count">{analytics.cash_payments}</span>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>Payment Types</h3>
                <div className="payment-types-grid">
                  <div className="type-item">
                    <span className="type-label">🏁 Deposit</span>
                    <span className="type-count">{analytics.deposit_payments}</span>
                  </div>
                  <div className="type-item">
                    <span className="type-label">📊 Partial</span>
                    <span className="type-count">{analytics.partial_payments}</span>
                  </div>
                  <div className="type-item">
                    <span className="type-label">✅ Final</span>
                    <span className="type-count">{analytics.final_payments}</span>
                  </div>
                  <div className="type-item">
                    <span className="type-label">💯 Full</span>
                    <span className="type-count">{analytics.full_payments}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payment-links' && linkAnalytics && (
          <div className="payment-links-tab">
            {/* Payment Links Metrics */}
            <div className="metrics-grid">
              <div className="metric-card links">
                <div className="metric-icon">🔗</div>
                <div className="metric-content">
                  <h3>Total Links</h3>
                  <div className="metric-value">{linkAnalytics.total_links}</div>
                  <div className="metric-subtitle">
                    {formatCurrency(linkAnalytics.total_link_value)} total value
                  </div>
                </div>
              </div>
              
              <div className="metric-card conversion">
                <div className="metric-icon">📈</div>
                <div className="metric-content">
                  <h3>Conversion Rate</h3>
                  <div className="metric-value">{linkAnalytics.conversion_rate}%</div>
                  <div className="metric-subtitle">
                    {linkAnalytics.completed_links} completed
                  </div>
                </div>
              </div>
              
              <div className="metric-card completed-value">
                <div className="metric-icon">✅</div>
                <div className="metric-content">
                  <h3>Completed Value</h3>
                  <div className="metric-value">{formatCurrency(linkAnalytics.completed_link_value)}</div>
                  <div className="metric-subtitle">
                    {formatCurrency(linkAnalytics.avg_link_amount)} average
                  </div>
                </div>
              </div>
              
              <div className="metric-card pending-value">
                <div className="metric-icon">⏳</div>
                <div className="metric-content">
                  <h3>Pending Value</h3>
                  <div className="metric-value">{formatCurrency(linkAnalytics.pending_link_value)}</div>
                  <div className="metric-subtitle">
                    {linkAnalytics.pending_links} pending links
                  </div>
                </div>
              </div>
            </div>

            {/* Link Status Breakdown */}
            <div className="charts-section">
              <div className="chart-card">
                <h3>Link Status Distribution</h3>
                <div className="status-grid">
                  <div className="status-item pending">
                    <span className="status-label">⏳ Pending</span>
                    <span className="status-count">{linkAnalytics.pending_links}</span>
                  </div>
                  <div className="status-item completed">
                    <span className="status-label">✅ Completed</span>
                    <span className="status-count">{linkAnalytics.completed_links}</span>
                  </div>
                  <div className="status-item expired">
                    <span className="status-label">⏰ Expired</span>
                    <span className="status-count">{linkAnalytics.expired_links}</span>
                  </div>
                  <div className="status-item cancelled">
                    <span className="status-label">❌ Cancelled</span>
                    <span className="status-count">{linkAnalytics.cancelled_links}</span>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>Link Types</h3>
                <div className="link-types-grid">
                  <div className="link-type-item">
                    <span className="link-type-label">🏁 Deposit</span>
                    <span className="link-type-count">{linkAnalytics.deposit_links}</span>
                  </div>
                  <div className="link-type-item">
                    <span className="link-type-label">✅ Final Payment</span>
                    <span className="link-type-count">{linkAnalytics.final_payment_links}</span>
                  </div>
                  <div className="link-type-item">
                    <span className="link-type-label">📊 Installment</span>
                    <span className="link-type-count">{linkAnalytics.installment_links}</span>
                  </div>
                  <div className="link-type-item">
                    <span className="link-type-label">🎯 Custom</span>
                    <span className="link-type-count">{linkAnalytics.custom_links}</span>
                  </div>
                </div>
              </div>
            </div>

            {linkAnalytics.avg_completion_hours && (
              <div className="insight-card">
                <h3>📊 Performance Insights</h3>
                <p>
                  <strong>Average completion time:</strong> {Math.round(linkAnalytics.avg_completion_hours)} hours
                </p>
                <p>
                  <strong>Conversion rate:</strong> {linkAnalytics.conversion_rate}% of payment links result in completed payments
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-tab">
            {/* Search */}
            <div className="search-section">
              <input
                type="text"
                placeholder="Search by confirmation number, client name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="search-input"
              />
              <button 
                onClick={fetchPaymentHistory}
                className="btn btn-primary"
              >
                🔍 Search
              </button>
            </div>

            {/* Payment History Table */}
            <div className="table-container">
              <table className="payment-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Confirmation</th>
                    <th>Payment Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDate(payment.created_at)}</td>
                      <td>
                        <div className="client-info">
                          <strong>{payment.first_name} {payment.last_name}</strong>
                          <small>{payment.client_email}</small>
                        </div>
                      </td>
                      <td className="amount">{formatCurrency(payment.amount)}</td>
                      <td>
                        <span className="payment-method">
                          {payment.payment_method === 'card' && '💳'}
                          {payment.payment_method === 'bank_transfer' && '🏦'}
                          {payment.payment_method === 'check' && '📝'}
                          {payment.payment_method === 'cash' && '💵'}
                          {payment.payment_method}
                        </span>
                      </td>
                      <td>{payment.payment_type}</td>
                      <td>{getStatusBadge(payment.status)}</td>
                      <td>
                        <code className="confirmation-number">
                          {payment.confirmation_number}
                        </code>
                      </td>
                      <td>
                        {payment.payment_link_id ? (
                          <div className="payment-link-info">
                            <span className="link-indicator">🔗</span>
                            <small>Via Payment Link</small>
                            {payment.payment_link_description && (
                              <div className="link-description">
                                {payment.payment_link_description}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="no-link">Direct Payment</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewPaymentDetails(payment)}
                          title="View secure payment details (requires 2FA)"
                        >
                          🔒 View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                  disabled={pagination.current_page <= 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.current_page} of {pagination.total_pages} 
                  ({pagination.total_records} total records)
                </span>
                <button 
                  onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                  disabled={pagination.current_page >= pagination.total_pages}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Secure Payment Details Modal */}
      {showSecureModal && selectedPayment && (
        <SecurePaymentDetailsModal
          payment={selectedPayment}
          onClose={closeSecureModal}
        />
      )}
    </div>
  );
};

export default PaymentReports;
